import React, { useState, useEffect } from "react";
import { BasePageModal } from "@/shared/components/modal/page-modal/base-page-modal";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { useDownloadTrades, SearchFilters } from "@/features/accounts/shared/hooks/use-download-trades";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { useTradesContext } from "@/features/accounts/shared/contexts/trades-context";
import { Checkbox } from "@/shared/components/check-box/check-box";
import { SimpleForm } from "@/shared/components/text-box/simple-form";

interface Props {
  fileName?: string,
  downloadAllAtOnce: boolean;
  onTapCompleteModal: () => void;
  onTapCloseModal: () => void;
}

// 明細ダウンロードモーダル
export const DownloadTradesModal= ({
  fileName = "Excel明細帳",
  downloadAllAtOnce,
  onTapCompleteModal,
  onTapCloseModal
}: Props) => {
  const {
    selectedAccount,
    targetAccountGroup,
  } = useAccountGroupsContext();
  const {
    tradesStartDate,
    tradesEndDate,
    searchTradeType,
    searchStartDate,
    searchEndDate,
    searchMinAmount,
    searchMaxAmount,
    searchMinBalance,
    searchMaxBalance,
    searchSummaryKeyword,
    searchMemoKeyword,
  } = useTradesContext();
  const {
    onClickDownload,
  } = useDownloadTrades({onTapCompleteModal});

  const [items, setItems] = useState<{
    mlinkAccountId: string;
    mlinkId: string;
    passbookType: string;
    passbookName: string;
    selectedStatus: number;
  }[]>(() => {
    if (downloadAllAtOnce) {
      return targetAccountGroup.accountsList.map(account => ({
        mlinkAccountId: account.mlinkAccountId,
        mlinkId: account.mlinkId,
        passbookType: account.passbookType,
        passbookName: account.passbookName,
        selectedStatus: 1, // まとめてダウンロードの場合は全て選択された状態にする
      }));
    } else  {
      return [];
    }
  })
  const [isActivateButton, setIsActivateButton] = useState(true);

  useEffect(() => {
    let count = 0;
    items.forEach(account => {
      if (account.selectedStatus === 1) {
        count++;
      }
    })
    if (downloadAllAtOnce) setIsActivateButton(count > 0);
  }, [items]);

  // チェックボックスのON/OFF
  const onClickCheck = (index: number) => {
    const updated = [...items];
    updated[index].selectedStatus = updated[index].selectedStatus === 1 ? 0 : 1;
    setItems(updated);
  };

  const passbookInfo = (): string => {
    type PassbookInfo = {
      mlink_account_id: string;
      mlink_id: string;
      passbook_type: string;
    }
    const list: PassbookInfo[] = [];
    if (downloadAllAtOnce) {
      items.forEach(account => {
        const element = {
          "mlink_account_id": account.mlinkAccountId,
          "mlink_id": account.mlinkId,
          "passbook_type": account.passbookType,
        }
        if (account.selectedStatus === 1) {
          list.push(element);
        }
      });
      return JSON.stringify(list);
    }
    else {
      if (!selectedAccount) {
        return "[]"
      }
      const element = {
        "mlink_account_id": selectedAccount.mlinkAccountId,
        "mlink_id": selectedAccount.mlinkId,
        "passbook_type": selectedAccount.passbookType,
      }
      list.push(element);
      return JSON.stringify(list);
    }
  };

  const setLabelClassName = (passbookType: string): string => {
    if (passbookType === "01") {
      return "btn-bank";
    } else if (passbookType === "19659" || passbookType === "19660" || passbookType === "03") {
      return "btn-card";
    } else if (passbookType === "10") {
      return "btn-emoney";
    } else if (passbookType === "20") {
      return "btn-app";
    } else if (passbookType === "201" || passbookType === "202") {
      return "btn-excel";
    } else if (passbookType === "101") {
      return "btn-ocr";
    } else if (passbookType === "09") {
      return "btn-bank";
    }
    return "";
  };

  if (downloadAllAtOnce && targetAccountGroup.accountsList.length === 0) {
    return (
      <BasePageModal
        title="明細のダウンロード"
        className="md-list-download"
        handleClose={() => {
          onTapCloseModal();
        }}
        bottomChildren={
          <BaseRoundButton
            buttonText="戻る"
            onClick={() => {
              onTapCloseModal();
            }}
          />
        }
      >
        <section>
          <h2>
            このグループには明細帳が設定されていません。
          </h2>
          <dl>
            <SimpleForm
              id="accountGroupName"
              value={targetAccountGroup.accountGroupsName}
              isReadOnly={true}
              title="グループ名"
              onChange={() => {}}
              labelEnable={true}
              descriptionType={true}
            />
          </dl>
        </section>
      </BasePageModal>
    );
  } else {
    return (
      <BasePageModal
        title="明細のダウンロード"
        className="md-group-download"
        handleClose={() => {
          onTapCloseModal();
        }}
        bottomChildren={
          <BaseRoundButton
            buttonText="明細をダウンロードする"
            id="download_trades_excel"
            loadingNegative={true}
            isActive={isActivateButton}
            onClick={() => {
              const accountGroupId = downloadAllAtOnce ? targetAccountGroup.accountGroupId : (selectedAccount?.accountGroupId || "");
              const searchFilters: SearchFilters = {
                tradeType: searchTradeType,
                startDate: searchStartDate || tradesStartDate || undefined,
                endDate: searchEndDate || tradesEndDate || undefined,
                minAmount: searchMinAmount,
                maxAmount: searchMaxAmount,
                minBalance: searchMinBalance,
                maxBalance: searchMaxBalance,
                summaryKeyword: searchSummaryKeyword || undefined,
                memoKeyword: searchMemoKeyword || undefined,
              };
              onClickDownload(
                accountGroupId,
                fileName,
                passbookInfo(),
                searchFilters,
                downloadAllAtOnce,
              );
            }}
          />
        }
      >
        <>
          <h2>
            {downloadAllAtOnce ? "以下をまとめてダウンロードします" : "現在の明細をダウンロードします"}
          </h2>
          {downloadAllAtOnce && (
          <>
          <dl>
            <SimpleForm
              id="accountGroupName"
              value={targetAccountGroup.accountGroupsName}
              isReadOnly={true}
              title="グループ名"
              onChange={() => {}}
              labelEnable={true}
              descriptionType={true}
            />
          </dl>
          <ul>
            {items.map((account, index) => (
              <li key={`${account.mlinkAccountId}-${account.mlinkId}`}>
                <Checkbox
                  label={<span>{account.passbookName}</span>}
                  id={`listDl-${index}`}
                  className="listDl"
                  handleChange={() => {onClickCheck(index)}}
                  checked={account.selectedStatus === 1}
                  labelClassName={`${setLabelClassName(account.passbookType)} label-text`}
                />
              </li>
            ))}
          </ul>
          </>
          )}
        </>
      </BasePageModal>
    );
  }
};
